import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const MidCta = () => {
  const blockRef = useRef(null);
  const tealSpanRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || !blockRef.current) return;

    const ctx = gsap.context(() => {
      // Stagger heading and paragraph only — button is animated separately
      gsap.from(blockRef.current.querySelectorAll("h2, p"), {
        y: 28,
        opacity: 0,
        stagger: 0.13,
        duration: 0.72,
        ease: "power3.out",
        scrollTrigger: {
          trigger: blockRef.current,
          start: "top 85%",
          once: true,
          onEnter: () => {
            // Teal glow pulses in after heading arrives
            setTimeout(() => {
              gsap.fromTo(
                tealSpanRef.current,
                { textShadow: "0 0 0px transparent" },
                {
                  textShadow: "0 0 22px rgba(123,181,177,0.55)",
                  duration: 0.4,
                  yoyo: true,
                  repeat: 1,
                  ease: "power2.inOut",
                }
              );
            }, 420);
          },
        },
      });
      // Button animates independently so StrictMode stagger interruption can't hide it
      gsap.from(btnRef.current, {
        y: 16,
        opacity: 0,
        duration: 0.6,
        ease: "power3.out",
        scrollTrigger: {
          trigger: blockRef.current,
          start: "top 85%",
          once: true,
        },
        delay: 0.28,
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <section
      className="midcta-section"
      style={{
        background: "transparent",
        padding: "clamp(48px, 6vw, 80px) clamp(20px, 5vw, 40px)",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        ref={blockRef}
        style={{
          maxWidth: "680px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <h2
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: "clamp(24px, 4vw, 36px)",
            fontWeight: 300,
            color: "#fff",
            margin: 0,
            letterSpacing: "-0.5px",
            lineHeight: 1.2,
          }}
        >
          Turn your idea into a{" "}
          <span ref={tealSpanRef} style={{ color: "#7BB5B1" }}>product people love</span>
        </h2>

        <p
          style={{
            fontSize: "15px",
            color: "rgba(255,255,255,0.45)",
            margin: 0,
            lineHeight: 1.7,
          }}
        >
          We work with startups and enterprises alike, from MVP to scale.
        </p>

        <Link
          ref={btnRef}
          to="/craft"
          style={{
            background: "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)",
            color: "#fff",
            padding: "14px 32px",
            borderRadius: "7px",
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 500,
            fontSize: "14px",
            border: "1px solid rgba(91,148,144,0.52)",
            cursor: "pointer",
            marginTop: "8px",
            transition: "all 0.2s ease",
            boxShadow: "0 0 18px rgba(91,148,144,0.28), 0 4px 12px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)",
            textDecoration: "none",
            display: "inline-flex",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "linear-gradient(135deg, #479a96 0%, #3a7a77 100%)";
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 0 30px rgba(91,148,144,0.46), 0 6px 18px rgba(0,0,0,0.40), inset 0 1px 0 rgba(255,255,255,0.16)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "linear-gradient(135deg, #3a7a77 0%, #2D5C5A 100%)";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 0 18px rgba(91,148,144,0.28), 0 4px 12px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.12)";
          }}
        >
          Our Recipes →
        </Link>
      </div>
    </section>
  );
};

export default MidCta;
